import { Subject, Observable } from 'rxjs';
import { Actions } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import {
    filter,
    map,
    mapTo,
    merge,
    share,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { AppGlobal } from '@core/utils/app';

/* eslint-disable prefer-arrow/prefer-arrow-functions */

export interface IObservableAction {
    status$: any;
    cancel: any;
    subscribe: any;
    toPromise: any;
    toObservable: any;
    map: any;
    pipe: any;
    nextAction: string;
}

/**
 * Decorates the action with an observable to manage it's lifecycle.
 * Action life cycle relies on the following events structure:
 *
 * {this.type}              Base event type
 * {this.type}_SUCCESS      Successful completion of the action
 * {this.type}_CANCEL       Silent completion of the action
 * {this.type}_ABORT        Action has been cancelled
 * {this.type}_ERROR        Action is in an error state
 *
 * @returns {(constructor:any)=>any}
 * @constructor
 */

export function ObservableAction(): any {
    return (constructor: any) => {
        /**
         * Extends constructors class.. wraps the original.
         */
        return class extends constructor {
            public status$;

            private source;
            private successAction: string;
            private cancelAction: string;
            private errorAction: string;
            private abortAction: string;
            private nextAction: string;

            constructor(...args) {
                super(...args);

                const injector = AppGlobal.injector();
                const actions$ = injector.get(Actions);
                const actionsubject$ = injector.get(ActionsSubject);
                this.status$ = new Subject();

                // Define action lifecycle events
                this.successAction = this.type + '_SUCCESS';
                this.cancelAction = this.type + '_CANCEL';
                this.errorAction = this.type + '_ERROR';
                this.abortAction = this.type + '_ABORT';
                this.nextAction = this.type + '_NEXT';

                const endActionTypes = [this.errorAction, this.abortAction];
                const destroyed$: Subject<any> = new Subject();

                // Define action observable for lifecycle
                this.source = Observable.create((observer) => {
                    // Hook into ngrx action stream
                    actionsubject$
                        .pipe(
                            filter((action: any) => action.type === this.type),
                            mapTo(true),
                            merge(
                                actionsubject$.pipe(
                                    filter(
                                        (action: any) =>
                                            action.type ===
                                                this.successAction ||
                                            action.type === this.cancelAction
                                    ),
                                    tap((abs) => {
                                        // Action success event
                                        observer.next(abs);
                                        observer.complete(abs);
                                    }),
                                    mapTo(false)
                                )
                            ),
                            merge(
                                actionsubject$.pipe(
                                    filter(
                                        (action: any) =>
                                            action.type === this.nextAction
                                    ),
                                    tap((abs) => {
                                        // Action next event
                                        observer.next(abs);
                                    }),
                                    mapTo(false)
                                )
                            ),
                            merge(
                                actionsubject$.pipe(
                                    filter((action: any) =>
                                        endActionTypes.some(
                                            (abortType) =>
                                                action.type === abortType
                                        )
                                    ),
                                    tap((abs) => {
                                        // Action abort / error events
                                        observer.error(abs);
                                    }),
                                    mapTo(false)
                                )
                            ),
                            takeUntil(destroyed$),
                            share()
                        )
                        .subscribe((isRunning) => {
                            // Publish action status change
                            this.status$.next(isRunning);
                        });

                    return () => destroyed$.next(null);
                });
            }

            /**
             * Abort action via action abort event.
             */
            abort() {
                const injector = AppGlobal.injector();
                const actions = injector.get(ActionsSubject);
                const abortActionType: string = this.type + '_ABORT';
                actions.next({ type: abortActionType });
            }

            /**
             * Allows subscription into internal action lifecycle.
             * @param args
             */
            subscribe(...args) {
                return this.source.subscribe(...args);
            }

            map(fn) {
                return this.source.pipe(map(fn));
            }

            pipe(...args) {
                return this.source.pipe(...args);
            }

            toPromise() {
                return this.source.toPromise();
            }

            toObservable() {
                return this.source;
            }

            next(payload) {
                return {
                    type: this.type + '_NEXT',
                    payload,
                };
            }

            success(response) {
                return { type: this.type + '_SUCCESS', payload: response };
            }

            error(error) {
                return { type: this.type + '_ERROR', payload: error };
            }

            cancel(response) {
                return { type: this.type + '_CANCEL', payload: response };
            }
        };
    };
}
